/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { useEffect } from "react";
import StickyToolCss from "components/stickyTools/StickyTools.css";
import { StickyToolItemContainer } from "containers/stickyTools/StickyToolsItemContainer";
import { stickyToolClickHelper, populateStickyToolVerbs } from "utils/StickyToolsUtils";
import { CSSTransition } from "react-transition-group";
import PropTypes from "prop-types";

const StickyToolsView = props => {
  const {
    stickyToolVerbs,
    stickyVerbDetails,
    shouldRenderCommentsDropin,
    shouldRenderPersonalCommentsDropin,
    isStickyToolPanelVisible,
    StickyToolsAddOnPanel,
    setBottomPanelHeight
  } = props;
  const previewConfig = {
    shouldRenderCommentsDropin,
    shouldRenderPersonalCommentsDropin
  };
  useEffect(() => {
    populateStickyToolVerbs(previewConfig).then(() => props.setStickyToolsLoaded(true));
  }, []);

  return (
    <CSSTransition
      in={isStickyToolPanelVisible}
      timeout={1000}
      classNames={{
        enter: StickyToolCss.stickyToolPanelEnter,
        enterActive: StickyToolCss.stickyToolPanelEnterActive,
        exit: StickyToolCss.stickyToolPanelExit,
        exitActive: StickyToolCss.stickyToolPanelExitActive
      }}
      mountOnEnter
      unmountOnExit
    >
      <div>
        {StickyToolsAddOnPanel && <StickyToolsAddOnPanel onLoad={() => setBottomPanelHeight()} />}
        <div id="stickyToolsView" className={StickyToolCss.StickyTools}>
          <ul className={StickyToolCss.StickyMobilePrimaryPanelTabList}>
            {stickyVerbDetails &&
              Object.keys(stickyVerbDetails).length > 0 &&
              Object.keys(stickyToolVerbs).map(verbId => {
                const showTool =
                  stickyVerbDetails[verbId] &&
                  stickyVerbDetails[verbId].show !== false &&
                  stickyVerbDetails[verbId].initialized;
                return (
                  showTool && (
                    <li key={verbId}>
                      <StickyToolItemContainer
                        verbName={verbId}
                        stickyToolsClickHelper={event => stickyToolClickHelper(event, { verbId })}
                        verbDetail={stickyVerbDetails[verbId]}
                      />
                    </li>
                  )
                );
              })}
          </ul>
        </div>
      </div>
    </CSSTransition>
  );
};

StickyToolsView.propTypes = {
  stickyVerbDetails: PropTypes.objectOf(
    PropTypes.shape({
      ariaLabel: PropTypes.string,
      icon: PropTypes.func,
      initialized: PropTypes.bool,
      label: PropTypes.string,
      show: PropTypes.bool,
      tooltip: PropTypes.string,
      verbId: PropTypes.string
    })
  ),
  stickyToolVerbs: PropTypes.object,
  shouldRenderCommentsDropin: PropTypes.bool,
  shouldRenderPersonalCommentsDropin: PropTypes.bool
};

StickyToolsView.defaultProps = {
  stickyToolVerbs: {},
  stickyVerbDetails: {},
  shouldRenderCommentsDropin: false,
  shouldRenderPersonalCommentsDropin: false
};

export default StickyToolsView;
